<template>
	<div class="storeDatesNavigation ml-06 flex items-center justify-between gap-05">
		<div class="flex items-center justify-between gap-03">
			<Tooltip :content="t(`navigation.navigateBackTooltip.${timelineResolution}.${canMoveBack ? 'possible' : 'notPossible'}`)">
				<Button icon="chevron-left" variant="quaternary" size="xs" :disabled="!canMoveBack" @click="goToPreviousPeriod(store.id)"></Button>
			</Tooltip>

			<span class="font-body-s min-w-[9rem] text-center text-ink-60">{{ visibleDatesRangeFormatted }}</span>

			<Tooltip :content="t(`navigation.navigateForwardTooltip.${timelineResolution}.${canMoveForward ? 'possible' : 'notPossible'}`)">
				<Button icon="chevron-right" variant="quaternary" size="xs" :disabled="!canMoveForward" @click="goToNextPeriod(store.id)"></Button>
			</Tooltip>
		</div>

		<div class="flex items-center gap-03">
			<Button variant="quaternary" size="xs" @click="goToToday">{{ t('navigation.today') }}</Button>
			<DatePicker
				v-model="selectedDateLocal"
				:min-date="dataRangeStartDate.toJSDate()"
				:max-date="dataRangeEndDate.toJSDate()"
				@open="isDatePickerOpen = true"
				@close="isDatePickerOpen = false"
			>
				<Button variant="quaternary" icon="calendar-today-outline" size="xs"></Button>
			</DatePicker>
		</div>
	</div>
</template>

<script setup lang="ts">
import { Button, DatePicker, isValidDate, Tooltip, useTranslation } from '@bluebrick/saas-ui'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, ref, toRefs } from 'vue'
import { monthYearFormattedShort } from '@/common/dateUtils'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { useCalendarStore } from '@/features/slicers/useCalendarStore'
import { useStoreDateRanges } from '@/features/slicers/useStoreDateRanges'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { type Store } from '@/types'

const props = defineProps<{
	store: Store
}>()

const { store } = toRefs(props)

const { trackEvent } = useTelemetry()
const { t } = useTranslation()
const { timelineResolution } = storeToRefs(usePreferencesStore())
const { visibleRangeStartDate, visibleRangeEndDate, canMoveBack, canMoveForward, selectedDate, goToToday, setSelectedDate } =
	useStoreDateRanges(store)
const { goToPreviousPeriod, goToNextPeriod } = useCalendarStore()
const { dataRangeStartDate, dataRangeEndDate } = storeToRefs(useCalendarStore())

const visibleDatesRangeFormatted = computed(
	() => `${monthYearFormattedShort(visibleRangeStartDate.value)} - ${monthYearFormattedShort(visibleRangeEndDate.value.startOf('day'))}`
)

const selectedDateLocal = computed({
	get: () => (selectedDate.value.isValid ? selectedDate.value.toJSDate() : undefined),
	set: (date) => {
		if (isValidDate(date)) {
			trackEvent('app', 'Set timeline date using calendar.', { selectedDate: date?.toISOString() })
			setSelectedDate(DateTime.utc(date.getFullYear(), date.getMonth() + 1, date.getDate()).startOf('day') as DateTime<true>, true)
		}
	}
})

const isDatePickerOpen = ref(false)
</script>
